import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import type { ItemQuantityPlan } from '../utils/quantityCalculation';
import { displayStatus } from '../utils/inventoryCheckLogic';

export interface QuantitySummaryProps {
    quantityPlans: Map<string, ItemQuantityPlan> | null;
    getAvailable: (itemTypeId: string) => number;
    getItemTypeName: (itemTypeId: string) => string;
    /** When provided, items are grouped by category (sorted A–Z) then by item name (A–Z), like operation task/inventory lists */
    getItemTypeCategory?: (itemTypeId: string) => string;
    getEventName?: (eventId: string) => string;
    getTrailerName?: (trailerId: string) => string;
}

const STATUS_ICON: Record<ReturnType<typeof displayStatus>, { icon: React.ReactNode; color: string }> = {
    success: { icon: <CheckCircleIcon fontSize="small" />, color: 'success.main' },
    warning: { icon: <WarningAmberIcon fontSize="small" />, color: 'warning.main' },
    error: { icon: <ErrorIcon fontSize="small" />, color: 'error.main' },
};

function statusIcon(need: number, have: number): { icon: React.ReactNode; color: string } {
    return STATUS_ICON[displayStatus(need, have)];
}

/**
 * Displays calculated quantities with simple collapsed view and expandable details (Phase 2).
 * Green = sufficient, yellow = low, red = insufficient.
 */
type PlanEntry = [string, ItemQuantityPlan];

function buildGroupedPlans(
    quantityPlans: Map<string, ItemQuantityPlan>,
    getItemTypeName: (id: string) => string,
    getItemTypeCategory?: (id: string) => string
): Array<{ categoryLabel: string; entries: PlanEntry[] }> {
    const entries: PlanEntry[] = Array.from(quantityPlans.entries());
    if (!getItemTypeCategory) {
        return [{ categoryLabel: '', entries: entries.sort((a, b) => getItemTypeName(a[0]).localeCompare(getItemTypeName(b[0]))) }];
    }
    const byCategory = new Map<string, PlanEntry[]>();
    for (const entry of entries) {
        const cat = getItemTypeCategory(entry[0]) || '';
        const list = byCategory.get(cat) ?? [];
        list.push(entry);
        byCategory.set(cat, list);
    }
    Array.from(byCategory.values()).forEach(list => {
        list.sort((a: PlanEntry, b: PlanEntry) => getItemTypeName(a[0]).localeCompare(getItemTypeName(b[0])));
    });
    const categoryLabels = Array.from(byCategory.keys()).sort((a, b) => a.localeCompare(b));
    return categoryLabels.map(categoryLabel => {
        const grouped = byCategory.get(categoryLabel);
        if (!grouped) throw new Error('QuantitySummary: missing category bucket');
        return {
            categoryLabel: categoryLabel || 'Uncategorized',
            entries: grouped,
        };
    });
}

export default function QuantitySummary({
    quantityPlans,
    getAvailable,
    getItemTypeName,
    getItemTypeCategory,
    getEventName: _getEventName = (id: string) => id,
    getTrailerName = (id: string) => id,
}: QuantitySummaryProps) {
    if (!quantityPlans || quantityPlans.size === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                Select sources and destinations to see quantity summary.
            </Typography>
        );
    }

    const grouped = buildGroupedPlans(quantityPlans, getItemTypeName, getItemTypeCategory);

    const renderPlans = (plans: PlanEntry[]) =>
        plans.map(([itemTypeId, plan]) => {
            const have = getAvailable(itemTypeId);
            const { icon, color } = statusIcon(plan.totalNeeded, have);
            const shortfall = have < plan.totalNeeded ? plan.totalNeeded - have : 0;
            return (
                <Box key={itemTypeId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ color }}>{icon}</Box>
                    <Typography variant="body2">
                        {getItemTypeName(itemTypeId)}: Need {plan.totalNeeded}, Have {have}
                        {shortfall > 0 && (
                            <Typography component="span" variant="body2" color="error.main" sx={{ ml: 0.5 }}>
                                Short {shortfall}
                            </Typography>
                        )}
                    </Typography>
                </Box>
            );
        });

    const renderDetails = (plans: PlanEntry[]) =>
        plans.map(([itemTypeId, plan]) => {
            const have = getAvailable(itemTypeId);
            const { icon, color } = statusIcon(plan.totalNeeded, have);
            return (
                <Box key={itemTypeId}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Box sx={{ color }}>{icon}</Box>
                        <Typography variant="body2" fontWeight={600}>
                            {getItemTypeName(itemTypeId)} (Need {plan.totalNeeded})
                        </Typography>
                    </Box>
                    {Array.from(plan.perTrailer.entries()).map(([trailerId, trailerPlan]) => (
                        <Typography key={trailerId} variant="body2" color="text.secondary" sx={{ pl: 3, fontSize: '0.8125rem' }}>
                            {getTrailerName(trailerId)}: {trailerPlan.quantity} — {trailerPlan.reason}
                        </Typography>
                    ))}
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 3, fontSize: '0.8125rem' }}>
                        Available: {have}
                        {have < plan.totalNeeded && (
                            <Typography component="span" color="error.main">
                                {' '}
                                (short by {plan.totalNeeded - have})
                            </Typography>
                        )}
                    </Typography>
                </Box>
            );
        });

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Quantity summary
            </Typography>
            <Accordion defaultExpanded={false} disableGutters sx={{ boxShadow: 0, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                        {grouped.map(({ categoryLabel, entries: planEntries }) => (
                            <Box key={categoryLabel || '_'} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {categoryLabel ? (
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ py: 0.5, fontWeight: 600 }}>
                                        {categoryLabel}
                                    </Typography>
                                ) : null}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: categoryLabel ? 1.5 : 0 }}>
                                    {renderPlans(planEntries)}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {grouped.map(({ categoryLabel, entries: planEntries }) => (
                            <Box key={categoryLabel || '_'}>
                                {categoryLabel ? (
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ py: 0.5, fontWeight: 600, mb: 1 }}>
                                        {categoryLabel}
                                    </Typography>
                                ) : null}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: categoryLabel ? 1.5 : 0 }}>
                                    {renderDetails(planEntries)}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}
